// NFC Security Manager - Advanced Security Features
import CryptoJS from 'crypto-js';
import nfcConfigManager from './nfcConfigManager.js';

class NFCSecurityManager {
  constructor() {
    this.securityLogs = [];
    this.encryptionKey = this._generateEncryptionKey();
    this.lastAccessTimes = new Map();
    this.failedAttempts = new Map();
    this.isInitialized = false;
  }

  // Initialize security manager
  async initialize() {
    if (this.isInitialized) return;

    try {
      console.log('🔒 Initializing NFC Security Manager...');

      // Load security configuration
      this.securityConfig = await nfcConfigManager.getSecurityConfig();

      // Initialize GDPR compliance
      await this._initializeGDPRCompliance();

      // Load existing security logs
      this._loadSecurityLogs();

      // Clean old data
      this._cleanOldData();

      this.isInitialized = true;
      console.log('✅ NFC Security Manager initialized');

    } catch (error) {
      console.error('❌ Failed to initialize NFC Security Manager:', error);
    }
  }

  // Generate encryption key for sensitive data
  _generateEncryptionKey() {
    // Use device-specific factors for key generation
    const factors = [
      navigator.userAgent,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset().toString(),
      'DIAS_NFC_SECURITY_2025'
    ];

    return CryptoJS.SHA256(factors.join('|')).toString();
  }

  // Encrypt sensitive data
  encryptData(data) {
    try {
      if (!this.securityConfig?.enableEncryption) {
        return data; // Return plain data if encryption disabled
      }

      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(data),
        this.encryptionKey
      ).toString();

      return encrypted;

    } catch (error) {
      console.error('Failed to encrypt data:', error);
      return data; // Fallback to plain data
    }
  }

  // Decrypt sensitive data
  decryptData(encryptedData) {
    try {
      if (!this.securityConfig?.enableEncryption) {
        return encryptedData; // Return plain data if encryption disabled
      }

      const bytes = CryptoJS.AES.decrypt(encryptedData, this.encryptionKey);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);

      return JSON.parse(decrypted);

    } catch (error) {
      console.error('Failed to decrypt data:', error);
      return null;
    }
  }

  // Validate NFC chip authenticity
  async validateNFCChip(nfcId, serialNumber = null) {
    try {
      console.log('🔍 Validating NFC chip authenticity:', nfcId);

      // Basic format validation
      if (!this._validateNFCFormat(nfcId)) {
        throw new Error('Invalid NFC ID format');
      }

      // Skip local validation - server will validate
      // Local validation removed for security (keys not stored in frontend)
      console.log('🔐 Skipping local NFC key validation - will be validated by server');

      // Check for replay attacks
      const isReplayAttack = this._detectReplayAttack(nfcId, serialNumber);
      if (isReplayAttack) {
        this._logSecurityEvent('replay_attack_detected', { nfcId, serialNumber });
        throw new Error('Potential replay attack detected');
      }

      // Cryptographic validation (if implemented on chips)
      if (serialNumber && this.securityConfig?.requireDeviceVerification) {
        const isCryptoValid = await this._validateCryptographicSignature(nfcId, serialNumber);
        if (!isCryptoValid) {
          this._logSecurityEvent('crypto_validation_failed', { nfcId, serialNumber });
          throw new Error('Cryptographic validation failed');
        }
      }

      // Rate limiting check
      this._checkRateLimit(nfcId);

      // Log successful validation
      this._logSecurityEvent('nfc_validated', { nfcId, serialNumber });

      return true;

    } catch (error) {
      console.error('❌ NFC validation failed:', error);
      this._logSecurityEvent('validation_failed', {
        nfcId,
        serialNumber,
        error: error.message
      });
      throw error;
    }
  }

  // Validate NFC ID format
  _validateNFCFormat(nfcId) {
    if (!nfcId || typeof nfcId !== 'string') return false;

    // Length check
    if (nfcId.length < 8 || nfcId.length > 50) return false;

    // Character validation (adjust based on your format)
    const validPattern = /^[A-Z0-9_\-@*%&+=]+$/;
    return validPattern.test(nfcId);
  }

  // Detect replay attacks
  _detectReplayAttack(nfcId, serialNumber) {
    const key = `${nfcId}_${serialNumber}`;
    const now = Date.now();
    const lastAccess = this.lastAccessTimes.get(key);

    if (lastAccess) {
      const timeDiff = now - lastAccess;
      // If same chip accessed within 5 seconds, potential replay
      if (timeDiff < 5000) {
        return true;
      }
    }

    // Update last access time
    this.lastAccessTimes.set(key, now);

    // Clean old entries (older than 1 hour)
    this.lastAccessTimes.forEach((time, k) => {
      if (now - time > 60 * 60 * 1000) {
        this.lastAccessTimes.delete(k);
      }
    });

    return false;
  }

  // Validate cryptographic signature (placeholder for future implementation)
  async _validateCryptographicSignature(nfcId, serialNumber) {
    try {
      // This would validate a cryptographic signature stored on the NFC chip
      // For now, return true as placeholder

      // Example implementation:
      // 1. Read signature from NFC chip
      // 2. Verify signature using public key
      // 3. Check timestamp/nonce to prevent replay

      console.log('🔐 Cryptographic validation (placeholder):', { nfcId, serialNumber });
      return true;

    } catch (error) {
      console.error('Cryptographic validation error:', error);
      return false;
    }
  }

  // Check rate limiting
  _checkRateLimit(nfcId) {
    const now = Date.now();
    const attempts = this.failedAttempts.get(nfcId) || [];

    // Clean old attempts (older than 1 hour)
    const recentAttempts = attempts.filter(time => now - time < 60 * 60 * 1000);

    // Check if too many attempts
    if (recentAttempts.length >= 10) {
      throw new Error('Rate limit exceeded for this NFC chip');
    }

    // Update attempts list
    this.failedAttempts.set(nfcId, recentAttempts);
  }

  // Record failed attempt
  recordFailedAttempt(nfcId, reason) {
    const now = Date.now();
    const attempts = this.failedAttempts.get(nfcId) || [];
    attempts.push(now);
    this.failedAttempts.set(nfcId, attempts);

    this._logSecurityEvent('failed_attempt', { nfcId, reason });
  }

  // Log security events
  _logSecurityEvent(eventType, data) {
    if (!this.securityConfig?.logNFCAccess) return;

    const event = {
      timestamp: Date.now(),
      type: eventType,
      data: data,
      userAgent: navigator.userAgent,
      ip: 'client-side', // Would need server-side for real IP
      sessionId: this._getSessionId()
    };

    this.securityLogs.push(event);

    // Keep only last 1000 events
    if (this.securityLogs.length > 1000) {
      this.securityLogs = this.securityLogs.slice(-1000);
    }

    // Save to localStorage (encrypted)
    this._saveSecurityLogs();

    console.log('🔒 Security event logged:', eventType, data);
  }

  // Get session ID
  _getSessionId() {
    let sessionId = sessionStorage.getItem('nfc_session_id');

    if (!sessionId) {
      sessionId = CryptoJS.lib.WordArray.random(16).toString();
      sessionStorage.setItem('nfc_session_id', sessionId);
    }

    return sessionId;
  }

  // Load security logs from localStorage
  _loadSecurityLogs() {
    try {
      const stored = localStorage.getItem('nfc_security_logs');
      if (stored) {
        const decrypted = this.decryptData(stored);
        if (decrypted && Array.isArray(decrypted)) {
          this.securityLogs = decrypted;
        }
      }
    } catch (error) {
      console.error('Failed to load security logs:', error);
      this.securityLogs = [];
    }
  }

  // Save security logs to localStorage
  _saveSecurityLogs() {
    try {
      const encrypted = this.encryptData(this.securityLogs);
      localStorage.setItem('nfc_security_logs', encrypted);
    } catch (error) {
      console.error('Failed to save security logs:', error);
    }
  }

  // Clean old data
  _cleanOldData() {
    const now = Date.now();
    const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days

    // Clean old security logs
    this.securityLogs = this.securityLogs.filter(log =>
      now - log.timestamp < maxAge
    );

    // Clean old failed attempts
    this.failedAttempts.forEach((attempts, nfcId) => {
      const recent = attempts.filter(time => now - time < maxAge);
      if (recent.length === 0) {
        this.failedAttempts.delete(nfcId);
      } else {
        this.failedAttempts.set(nfcId, recent);
      }
    });

    // Clean old access times
    this.lastAccessTimes.forEach((time, key) => {
      if (now - time > maxAge) {
        this.lastAccessTimes.delete(key);
      }
    });
  }

  // Initialize GDPR compliance
  async _initializeGDPRCompliance() {
    // Check if user has consented to data processing
    const consent = localStorage.getItem('gdpr_nfc_consent');

    if (!consent) {
      console.log('📋 GDPR: No NFC data processing consent found');

      // Dispatch event for UI to show consent dialog
      window.dispatchEvent(new CustomEvent('gdpr-consent-required', {
        detail: {
          type: 'nfc_processing',
          description: 'We need your consent to process NFC data for premium features.'
        }
      }));
    }
  }

  // Record GDPR consent
  recordGDPRConsent() {
    const consent = {
      granted: true,
      timestamp: Date.now(),
      version: '1.0.0'
    };

    localStorage.setItem('gdpr_nfc_consent', JSON.stringify(consent));
    this._logSecurityEvent('gdpr_consent_granted', consent);

    console.log('✅ GDPR consent recorded for NFC processing');
  }

  // Export user data (GDPR right to data portability)
  exportUserData(userEmail) {
    try {
      const userData = {
        email: userEmail,
        exportDate: new Date().toISOString(),
        nfcLogs: this.securityLogs.filter(log =>
          log.data?.userEmail === userEmail
        ),
        gdprConsent: localStorage.getItem('gdpr_nfc_consent')
      };

      // Create downloadable file
      const blob = new Blob([JSON.stringify(userData, null, 2)], {
        type: 'application/json'
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nfc-data-export-${userEmail}-${Date.now()}.json`;
      a.click();

      URL.revokeObjectURL(url);

      this._logSecurityEvent('data_exported', { userEmail });

    } catch (error) {
      console.error('Failed to export user data:', error);
    }
  }

  // Delete user data (GDPR right to erasure)
  deleteUserData(userEmail) {
    try {
      // Remove user-specific logs
      this.securityLogs = this.securityLogs.filter(log =>
        log.data?.userEmail !== userEmail
      );

      // Remove from localStorage
      this._saveSecurityLogs();

      // Remove other user-specific data
      Object.keys(localStorage).forEach(key => {
        if (key.includes(userEmail) || key.includes('nfc_ownership')) {
          localStorage.removeItem(key);
        }
      });

      this._logSecurityEvent('data_deleted', { userEmail });

      console.log('🗑️ User data deleted for GDPR compliance:', userEmail);

    } catch (error) {
      console.error('Failed to delete user data:', error);
    }
  }

  // Get security report
  getSecurityReport() {
    const now = Date.now();
    const last24h = now - (24 * 60 * 60 * 1000);
    const last7days = now - (7 * 24 * 60 * 60 * 1000);

    const recent24h = this.securityLogs.filter(log => log.timestamp > last24h);
    const recent7days = this.securityLogs.filter(log => log.timestamp > last7days);

    const eventCounts = {};
    recent7days.forEach(log => {
      eventCounts[log.type] = (eventCounts[log.type] || 0) + 1;
    });

    return {
      totalEvents: this.securityLogs.length,
      events24h: recent24h.length,
      events7days: recent7days.length,
      eventTypes: eventCounts,
      failedAttempts: this.failedAttempts.size,
      lastCleanup: this._getLastCleanupTime(),
      gdprCompliant: !!localStorage.getItem('gdpr_nfc_consent')
    };
  }

  // Get last cleanup time
  _getLastCleanupTime() {
    const stored = localStorage.getItem('nfc_last_cleanup');
    return stored ? parseInt(stored) : null;
  }

  // Perform security audit
  async performSecurityAudit() {
    console.log('🔍 Performing NFC security audit...');

    const audit = {
      timestamp: Date.now(),
      encryption: this.securityConfig?.enableEncryption || false,
      logging: this.securityConfig?.logNFCAccess || false,
      gdprCompliance: !!localStorage.getItem('gdpr_nfc_consent'),
      securityLogs: this.securityLogs.length,
      rateLimiting: this.failedAttempts.size,
      recommendations: []
    };

    // Add recommendations
    if (!audit.encryption) {
      audit.recommendations.push('Enable data encryption for enhanced security');
    }

    if (!audit.logging) {
      audit.recommendations.push('Enable security logging for audit trails');
    }

    if (!audit.gdprCompliance) {
      audit.recommendations.push('Obtain GDPR consent for data processing');
    }

    if (this.securityLogs.length === 0) {
      audit.recommendations.push('Security logging appears to be inactive');
    }

    console.log('🔒 Security audit completed:', audit);
    return audit;
  }
}

export default new NFCSecurityManager();